import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Clock, Send } from 'lucide-react';

// Styled components with modern fonts
const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Outfit", "Inter", sans-serif',
  fontWeight: 300,
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.8rem',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '4rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '5rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '6rem',
  },
}));

const MarketingSpan = styled('span')(({ theme }) => ({
  fontFamily: '"Outfit", "Inter", sans-serif',
  fontWeight: 600,
  color: theme.palette.grey[700],
  marginLeft: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(1),
  },
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 300,
  color: theme.palette.grey[500],
  maxWidth: 700,
  margin: '0 auto',
  lineHeight: 1.6,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: theme.spacing(0, 2),
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.3rem',
  },
}));

const ComingSoonText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Outfit", "Inter", sans-serif',
  fontWeight: 300,
  letterSpacing: '0.05em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.8rem',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '2.2rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '3rem',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: '"Inter", sans-serif',
    backgroundColor: theme.palette.grey[50],
    borderRadius: '9999px',
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.95rem',
    },
    '& fieldset': {
      borderColor: theme.palette.grey[200],
      transition: 'border-color 0.3s ease',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.grey[900],
      borderWidth: '1px',
    },
  },
  '& input': {
    padding: theme.spacing(1.5, 3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 3),
    },
    '&::placeholder': {
      color: theme.palette.grey[400],
      opacity: 1,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 300,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  backgroundColor: theme.palette.grey[900],
  color: 'white',
  borderRadius: '9999px',
  padding: theme.spacing(1.2, 3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5, 4),
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.grey[800],
    transform: 'translateY(-2px)',
    boxShadow: `0 10px 20px ${alpha(theme.palette.grey[900], 0.2)}`,
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const FooterText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 300,
  color: theme.palette.grey[400],
  fontSize: '0.8rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.875rem',
  },
}));

export const HomePage = () => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Animation styles
  const fadeInUp = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const fadeIn = {
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 1s ease-in-out',
  };

  const scaleIn = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'scale(1)' : 'scale(0.9)',
    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s',
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        py: { xs: 2, sm: 3, md: 4 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements with animation */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.grey[300]}, transparent)`,
          transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
          transition: 'transform 1s ease-in-out 0.5s',
          transformOrigin: 'left',
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.grey[300]}, transparent)`,
          transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
          transition: 'transform 1s ease-in-out 0.5s',
          transformOrigin: 'right',
        }}
      />

      <Container 
        maxWidth="lg" 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          {/* Logo with animation */}
          <Box sx={fadeInUp}>
            <LogoText variant="h1">
              jinniex
              <MarketingSpan>Marketing</MarketingSpan>
            </LogoText>
          </Box>

          {/* Marketing phrase with animation */}
          <Box sx={{ ...fadeInUp, transitionDelay: '0.1s' }}>
            <SubtitleText>
              Where creativity meets strategy to elevate your brand's digital presence
            </SubtitleText>
          </Box>

          {/* Coming Soon with animation */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: { xs: 1.5, sm: 2 }, 
              mb: { xs: 4, sm: 5, md: 6 },
              mt: { xs: 3, sm: 4 },
              ...fadeInUp,
              transitionDelay: '0.2s',
            }}
          >
            <Box
              component="span"
              sx={{
                animation: 'spin 20s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
                display: 'inline-flex',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.6s ease 0.4s',
              }}
            >
              <Clock 
                size={24} 
                color={theme.palette.grey[400]}
                style={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
            <ComingSoonText variant="h2">
              Coming Soon
            </ComingSoonText>
          </Box>

          {/* Email Form with animation */}
          <Box 
            sx={{ 
              maxWidth: { xs: '90%', sm: 400, md: 450 }, 
              mx: 'auto',
              ...scaleIn,
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: { xs: 1.5, sm: 1 },
              alignItems: 'center',
            }}>
              <StyledTextField
                fullWidth
                placeholder="Enter your email for updates"
                variant="outlined"
                size="small"
              />
              <StyledButton
                variant="contained"
                disableElevation
                endIcon={<Send size={16} />}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                Notify Me
              </StyledButton>
            </Box>
            <Box sx={{ ...fadeIn, transitionDelay: '0.6s' }}>
              <FooterText 
                variant="caption" 
                display="block" 
                sx={{ 
                  mt: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                }}
              >
                Be the first to know when we launch
              </FooterText>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer with animation */}
      <Box sx={{ ...fadeIn, transitionDelay: '0.7s' }}>
        <FooterText variant="body2" align="center">
          © 2026 jinniex Marketing. All rights reserved.
        </FooterText>
      </Box>
    </Box>
  );
};

export default HomePage;